/**
 * ExpiringStorageService provides the ability to store local data for a set amount of time
 * Uses localForage library to store in indexedDB when available
 */
import { Injectable } from '@angular/core';
import * as localforage from 'localforage';
import { filter } from 'lodash';
import { promiseMap, toDate } from '@shared/utils';

@Injectable()
export class ExpiringStorageService {
  public namespace: string = 'expirable_';
  /**
   * Storage getter.
   *
   * @param key - property name.
   * @return Parsed data.
   */
  public getItem(key: string): Promise<any> {
    return localforage
      .getItem(this.namespace + key)
      .then((wrapperObject: any) => {
        if (wrapperObject) {
          if (toDate().getTime() < wrapperObject.expiration) {
            return wrapperObject.value;
          }
          this.removeItem(this.namespace + key);

          return undefined;
        }

        return undefined;
      })
      .catch(() => {
        return undefined;
      });
  }
  /**
   * Storage setter.
   *
   * @param key - property name.
   * @param value - property value.
   * @param expirationSeconds - default value as 1 day
   * @return Parsed data.
   */
  public setItem(
    key: string,
    value,
    expirationSeconds: number = 86400,
  ): Promise<any> {
    const wrapperObject: any = {
      value,
      expiration: toDate().getTime() + expirationSeconds * 1000,
    };

    return localforage
      .setItem(this.namespace + key, wrapperObject)
      .then((setResult) => {
        return setResult.value;
      })
      .catch(() => {
        return undefined;
      });
  }
  /**
   * Remove item from storage by property name.
   *
   * @param key - property name.
   */
  public removeItem(key: string): Promise<boolean> {
    return localforage
      .removeItem(this.namespace + key)
      .then(() => {
        return true;
      })
      .catch(() => {
        return false;
      });
  }

  public async removeAllItemsWithPrefix(prefix: string): Promise<any> {
    const keys = await localforage.keys();
    const itemsToDelete = filter(keys, (key: string) =>
      key.startsWith(`${this.namespace}${prefix}`),
    );
    const itemNamesWithoutPrefix = itemsToDelete.map((item) =>
      item.replace(this.namespace, ''),
    );

    await promiseMap(itemNamesWithoutPrefix, (key) => this.removeItem(key));
  }
}
